import { Box, ListItemText, MenuItem, MenuList, SxProps, Theme, styled } from '@mui/material';
import { PoolLineClampTypography, PoolRow, PoolTypography } from '@synergy/components-next';
import { ReactElement, ReactNode } from 'react';
import { SearchIcon } from '@synergy/theme-next';
import { useIntl } from 'react-intl';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import { SearchResultItem } from './search-autocomplete-hook';

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    [theme.breakpoints.up('sm')]: {
        minHeight: theme.spacing(4.5),
    },
}));

const Header = styled(Box)(({ theme }) => ({
    fontWeight: 600,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    height: theme.spacing(4.5),
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
}));

const SearchAutocompleteListItem = ({
    item,
    search,
    onClick,
    icon,
}: {
    item: SearchResultItem;
    search?: string;
    onClick?: (item: SearchResultItem) => void;
    icon?: ReactNode;
}): ReactElement => {
    const intl = useIntl();

    let nameParts: { text: string; highlight: boolean }[] = [{ text: item.name, highlight: false }];
    if (search) {
        const nameMatches = match(item.name, search, { insideWords: true });
        nameParts = parse(item.name, nameMatches);
    }

    return (
        <StyledMenuItem key={item.id} onClick={() => onClick?.(item)}>
            <PoolRow sx={{ '& > svg:first-of-type': { color: theme => theme.palette.grey[500] } }}>
                {icon ?? <SearchIcon />}
                <PoolLineClampTypography color="common.black">
                    {nameParts.map((namePart, index) => (
                        <Box key={index} component="span" color={namePart.highlight ? 'primary.main' : undefined}>
                            {namePart.text}
                        </Box>
                    ))}
                </PoolLineClampTypography>
                <PoolTypography variant="caption">{item.count && ` (${intl.formatNumber(item.count)})`}</PoolTypography>
            </PoolRow>
        </StyledMenuItem>
    );
};

export interface SearchAutocompleteListProps {
    items: SearchResultItem[];
    count: number;
    title: ReactNode;
    actionLabel?: ReactNode;
    search?: string;
    onItemClick: (item: SearchResultItem) => void;
    onActionClick?: () => void;
    sx?: SxProps<Theme>;
    icon?: ReactNode;
}

export const SearchAutocompleteList = (props: SearchAutocompleteListProps): ReactElement => {
    const { items, count, title, actionLabel, search, sx, onItemClick, onActionClick, icon } = props;

    return (
        <MenuList sx={{ py: 0, ...sx }}>
            <Header>{title}</Header>
            {items?.slice(0, count).map(item => (
                <SearchAutocompleteListItem
                    item={item}
                    key={item.id}
                    onClick={onItemClick}
                    search={search}
                    icon={icon}
                />
            ))}
            {actionLabel && (
                <StyledMenuItem onClick={onActionClick}>
                    <ListItemText>
                        <PoolTypography variant="button">{actionLabel}</PoolTypography>
                    </ListItemText>
                </StyledMenuItem>
            )}
        </MenuList>
    );
};
